import React, { useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput, getCountryCodeByName } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { AddressSearch } from '@/components/maps/AddressSearch';
import { GeocodingResult } from '@/components/maps/mapbox';
import SiteLocationMap from '@/components/maps/SiteLocationMap';
import { useUpdateCustomer } from '@/features/Customers/hooks/useUpdateCustomer';
import { AddressDto } from '@/types/common-types';

// Form validation schema
const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  accountNumber: z.string().optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phoneNumber: z.string().optional(),
  address: z
    .object({
      addressLine1: z.string().optional(),
      addressLine2: z.string().optional(),
      city: z.string().optional(),
      postCode: z.string().optional(),
      region: z.string().optional(),
      country: z.string().optional(),
      lat: z.number().optional(),
      lon: z.number().optional(),
    })
    .optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface Customer {
  id: string;
  name: string;
  hasContracts: boolean;
  accountNumber: string;
  archived: boolean;
  email?: string;
  phoneNumber?: string;
  address?: AddressDto;
}

interface EditCustomerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer?: Customer;
  onCustomerUpdate?: (updatedCustomer: Partial<Customer>) => void;
}

export default function EditCustomerModal({
  open,
  onOpenChange,
  customer,
  onCustomerUpdate,
}: EditCustomerModalProps) {
  const [mapLat, setMapLat] = useState<number | undefined>(customer?.address?.lat ?? -36.8509);
  const [mapLon, setMapLon] = useState<number | undefined>(customer?.address?.lon ?? 174.7645);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form with customer data or default values
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: customer?.name || '',
      accountNumber: customer?.accountNumber || '',
      email: customer?.email || '',
      phoneNumber: customer?.phoneNumber || '',
      address: {
        addressLine1: customer?.address?.addressLine1 || '',
        addressLine2: customer?.address?.addressLine2 || '',
        city: customer?.address?.city || '',
        postCode: customer?.address?.postCode || '',
        region: customer?.address?.region || '',
        country: customer?.address?.country || '',
        lat: customer?.address?.lat ?? -36.8509,
        lon: customer?.address?.lon ?? 174.7645,
      },
    },
  });

  // Update form values when customer prop changes
  useEffect(() => {
    if (customer) {
      form.reset({
        name: customer.name,
        accountNumber: customer.accountNumber,
        email: customer.email || '',
        phoneNumber: customer.phoneNumber || '',
        address: {
          addressLine1: customer.address?.addressLine1 || '',
          addressLine2: customer.address?.addressLine2 || '',
          city: customer.address?.city || '',
          postCode: customer.address?.postCode || '',
          region: customer.address?.region || '',
          country: customer.address?.country || '',
          lat: customer.address?.lat ?? -36.8509,
          lon: customer.address?.lon ?? 174.7645,
        },
      });
      setMapLat(customer.address?.lat ?? -36.8509);
      setMapLon(customer.address?.lon ?? 174.7645);
    }
  }, [customer, form]);

  const updateCustomer = useUpdateCustomer();

  function onSubmit(values: FormValues) {
    if (!customer?.id) {
      console.error('Customer ID is required for update');
      return;
    }

    setIsLoading(true);
    updateCustomer.mutate(
      {
        id: customer.id,
        data: values,
      },
      {
        onSuccess: (response) => {
          console.log('Customer updated successfully');
          if (response.customer) {
            onCustomerUpdate?.(response.customer);
          }
          onOpenChange(false);
        },
        onError: (error) => {
          console.error('Error updating customer:', error);
        },
        onSettled: () => {
          setIsLoading(false);
        },
      }
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Customer</DialogTitle>
        </DialogHeader>
        <p className="p-4 pb-0">Update customer information and address details.</p>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-1">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Customer Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Customer name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="col-span-1">
                  <FormField
                    control={form.control}
                    name="accountNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Account Number</FormLabel>
                        <FormControl>
                          <Input placeholder="Account number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="Email address" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <PhoneInput
                          value={field.value}
                          onChange={field.onChange}
                          defaultCountryCode={getCountryCodeByName(customer?.address?.country)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-2">
                <Label>Address & Location</Label>
                <div className="grid min-h-[320px] grid-cols-2 gap-4">
                  {/* Left side - Address inputs and details */}
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="address.addressLine1"
                      render={() => (
                        <FormItem>
                          <FormControl>
                            <AddressSearch
                              onSelect={(result: GeocodingResult) => {
                                const newAddress = {
                                  addressLine1: result.address || '',
                                  addressLine2: form.getValues('address.addressLine2') || '',
                                  city: result.city || '',
                                  region: result.state || '',
                                  country: result.country || '',
                                  postCode: result.postcode || '',
                                  lat: result.center[1],
                                  lon: result.center[0],
                                };
                                form.setValue('address', newAddress, { shouldValidate: true });
                                setMapLat(result.center[1]);
                                setMapLon(result.center[0]);
                              }}
                              placeholder="Search for an address"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="address.addressLine2"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input placeholder="Address line 2 (optional)" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="rounded-lg border border-gray-700 bg-[#111] p-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-xs font-medium text-gray-400">Address Line 1</div>
                          <div className="text-sm text-white">
                            {form.watch('address.addressLine1') || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-400">Address Line 2</div>
                          <div className="text-sm text-white">
                            {form.watch('address.addressLine2') || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-400">City</div>
                          <div className="text-sm text-white">
                            {form.watch('address.city') || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-400">Region</div>
                          <div className="text-sm text-white">
                            {form.watch('address.region') || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-400">Post Code</div>
                          <div className="text-sm text-white">
                            {form.watch('address.postCode') || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-400">Country</div>
                          <div className="text-sm text-white">
                            {form.watch('address.country') || '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right side - Map */}
                  <div className="h-[240px]">
                    <SiteLocationMap
                      coordinates={{
                        lat: mapLat || -36.8509,
                        lng: mapLon || 174.7645,
                      }}
                      initialAddress={form.watch('address.addressLine1')}
                      onCoordinatesChange={(coords) => {
                        setMapLat(coords.lat);
                        setMapLon(coords.lng);
                        const newAddress = {
                          ...form.getValues('address'),
                          lat: coords.lat,
                          lon: coords.lng,
                        };
                        form.setValue('address', newAddress, { shouldValidate: true });
                      }}
                      onAddressChange={(address, lat, lon) => {
                        // Update form with the new address information
                        const addressParts = address.split(',');
                        const newAddress = {
                          ...form.getValues('address'),
                          addressLine1: addressParts[0]?.trim() || '',
                          city: addressParts[1]?.trim() || form.getValues('address.city') || '',
                          region: addressParts[2]?.trim() || form.getValues('address.region') || '',
                          country:
                            addressParts[addressParts.length - 1]?.trim() ||
                            form.getValues('address.country') ||
                            '',
                          lat,
                          lon,
                        };
                        form.setValue('address', newAddress, { shouldValidate: true });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t p-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                <Save className="mr-2 h-4 w-4" />
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
